import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftIcon, CopyIcon, QrCodeIcon } from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import PinAnalytics from "@/components/PinAnalytics";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const ST = { active: "border-primary/40 text-primary", pending_review: "border-accent/40 text-accent", unclaimed: "border-white/25 text-white/50", unallocated: "border-white/20 text-white/40" };

export default function AdminCodeAnalytics() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const inv = await api.get("/admin/inventory", { params: { q: code } });
      setMeta((inv.data.items || []).find((i) => i.code === code) || { code, status: "unknown" });
      const a = await api.get(`/admin/customers/${code}/analytics`);
      setStats(a.data);
    } catch { toast.error("Could not load analytics"); navigate("/admin/customers"); }
  }, [code, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!meta) return <Layout><p className="font-mono text-sm text-primary animate-pulse">LOADING…</p></Layout>;

  const scanUrl = resolveUrl(code);

  return (
    <Layout>
      <button data-testid="admin-analytics-back" onClick={() => navigate("/admin/customers")} className="flex items-center gap-1.5 font-mono text-xs text-white/50 hover:text-primary transition-colors mb-6">
        <ArrowLeftIcon weight="bold" /> CUSTOMERS
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-4xl tracking-tighter leading-none">/{code}</h1>
            <Badge variant="outline" className={`font-mono text-[10px] uppercase ${ST[meta.status] || ""}`}>{meta.status}</Badge>
          </div>
          {meta.pending_owner_email && <p className="font-mono text-sm text-white/50 mt-2">{meta.pending_owner_email}</p>}
        </div>
        {meta.status === "active" && (
          <a href={scanUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-primary/40 text-primary px-4 py-2.5 text-xs font-mono hover:bg-primary/10 transition-colors"><QrCodeIcon weight="bold" /> OPEN LIVE LINK</a>
        )}
      </div>

      <div className="border border-border bg-card flex items-center justify-between gap-4 px-6 py-4 mb-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Permanent scan URL</p>
          <p className="font-mono text-sm truncate text-white/80">{scanUrl}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(scanUrl); toast.success("Copied"); }} className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs shrink-0">
          <CopyIcon weight="bold" className="mr-1.5" /> COPY
        </Button>
      </div>
      {meta.destination_url && (
        <div className="border border-border bg-card px-6 py-4 mb-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Points to</p>
          <p className="font-mono text-sm truncate text-white/70">{meta.destination_url}</p>
        </div>
      )}

      <PinAnalytics stats={stats} />
    </Layout>
  );
}
